import { useEffect, useState, useMemo, useRef } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useUnsavedGuard } from '../../hooks/useUnsavedWork.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Ban, RotateCcw, Trash2 } from 'lucide-react';
import { formatDays } from '@/lib/utils';

const ACCRUAL_METHODS = [
  { value: 'annual_grant', label: 'Annual grant' },
  { value: 'anniversary_grant', label: 'Anniversary grant' },
  { value: 'monthly_accrual', label: 'Monthly accrual' },
];

const CARRY_OVER_RULES = [
  { value: 'none', label: 'None' },
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'capped', label: 'Capped' },
  { value: 'expires', label: 'Expires' },
];

function formatEnum(s) {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function LeaveTypesPage() {
  const toast = useToast();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingType, setEditingType] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listLeaveTypes();
      setLeaveTypes(res.leaveTypes || []);
    } catch (err) {
      toast.error(`Failed to load leave types: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = leaveTypes;
    if (filter === 'active') list = list.filter((t) => t.isActive !== false);
    else if (filter === 'inactive') list = list.filter((t) => t.isActive === false);

    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.displayName?.toLowerCase().includes(s) ||
          t.rowKey?.toLowerCase().includes(s)
      );
    }
    return list.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [leaveTypes, search, filter]);

  async function toggleActive(t, e) {
    e.stopPropagation();
    const isActive = t.isActive !== false;
    const verb = isActive ? 'Deactivate' : 'Reactivate';
    const msg = isActive
      ? `Deactivate "${t.displayName}"? It will no longer be available for new requests (existing requests are preserved).`
      : `Reactivate "${t.displayName}"? Employees will be able to request this leave type again.`;
    if (!window.confirm(msg)) return;
    try {
      if (isActive) {
        await adminApi.deleteLeaveType(t.rowKey);
      } else {
        // Reactivate by re-upserting with isActive=true, preserving existing fields.
        await adminApi.upsertLeaveType(t.rowKey, {
          displayName: t.displayName,
          color: t.color,
          icon: t.icon,
          defaultAllowance: t.defaultAllowance,
          isActive: true,
          policy: t.policy || {},
        });
      }
      toast.success(`${verb}d "${t.displayName}"`);
      await load();
    } catch (err) {
      toast.error(`${verb} failed: ${err.message}`);
    }
  }

  async function hardDelete(t, e) {
    e.stopPropagation();
    const confirmMsg =
      `PERMANENTLY delete "${t.displayName}"?\n\n` +
      `This cannot be undone. If this type is referenced by any existing ` +
      `leave requests or balances, the server will block the deletion and ` +
      `ask you to deactivate instead.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await adminApi.hardDeleteLeaveType(t.rowKey);
      toast.success(`Deleted "${t.displayName}" permanently`);
      await load();
    } catch (err) {
      // If the server refused because of references, offer a force option.
      const msg = err.message || '';
      if (/referenced by/i.test(msg)) {
        const force = window.confirm(
          `${msg}\n\nForce delete anyway? Historical records will lose their type label.`,
        );
        if (!force) return;
        try {
          await adminApi.hardDeleteLeaveType(t.rowKey, true);
          toast.success(`Force-deleted "${t.displayName}"`);
          await load();
        } catch (err2) {
          toast.error(`Delete failed: ${err2.message}`);
        }
      } else {
        toast.error(`Delete failed: ${msg}`);
      }
    }
  }

  const columns = [
    {
      key: 'displayName',
      label: 'Name',
      render: (t) => (
        <div className="flex items-center gap-3">
          <div
            className="w-4 h-4 rounded-full shrink-0"
            style={{ background: t.color || '#64748b' }}
          />
          <div>
            <div className="font-medium">{t.displayName}</div>
            <div className="text-xs text-muted-foreground">{t.rowKey}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'defaultAllowance',
      label: 'Allowance',
      render: (t) => `${formatDays(t.defaultAllowance ?? 0)} days`,
    },
    {
      key: 'accrualMethod',
      label: 'Accrual',
      render: (t) => formatEnum(t.policy?.accrualMethod),
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (t) => (
        <Badge variant={t.isActive === false ? 'danger' : 'success'}>
          {t.isActive === false ? 'Inactive' : 'Active'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (t) => {
        const isActive = t.isActive !== false;
        return (
          <div className="flex items-center gap-1.5">
            <Button
              variant={isActive ? 'danger' : 'outline'}
              size="sm"
              onClick={(e) => toggleActive(t, e)}
              className="h-7"
            >
              {isActive ? <Ban className="h-3 w-3" /> : <RotateCcw className="h-3 w-3" />}
              {isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => hardDelete(t, e)}
              className="h-7 text-muted-foreground hover:text-destructive"
              title={`Permanently delete "${t.displayName}"`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Leave Types</h1>
          <p className="text-muted-foreground">
            {leaveTypes.length} total &middot;{' '}
            {leaveTypes.filter((t) => t.isActive !== false).length} active
          </p>
        </div>
        <Button onClick={() => setEditingType({})}>Add leave type</Button>
      </div>

      <div className="flex gap-3 mb-5 items-end">
        <Input
          label="Search"
          placeholder="Search types..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-[400px]"
        />
        <Select
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
          ]}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage={
          leaveTypes.length === 0
            ? 'No leave types yet. Click "Add leave type" to create one.'
            : 'No types match your filters.'
        }
        onRowClick={(t) => setEditingType(t)}
        keyFn={(t) => t.rowKey}
      />

      {editingType !== null && (
        <LeaveTypeEditModal
          type={editingType}
          onClose={() => setEditingType(null)}
          onSaved={async () => {
            setEditingType(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function LeaveTypeEditModal({ type, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !type.rowKey;

  const [form, setForm] = useState({
    typeId: type.rowKey || '',
    displayName: type.displayName || '',
    color: type.color || '#64748b',
    icon: type.icon || '',
    defaultAllowance: type.defaultAllowance ?? 20,
    isActive: type.isActive !== false,
    accrualMethod: type.policy?.accrualMethod || 'annual_grant',
    requiresApproval: type.policy?.requiresApproval !== false,
    approvalMode: type.policy?.approvalMode || 'chain',
    countsAgainstBalance: type.policy?.countsAgainstBalance !== false,
    maxConsecutiveDays: type.policy?.maxConsecutiveDays ?? '',
    minNoticeDays: type.policy?.minNoticeDays ?? 0,
    carryOverRule: type.policy?.carryOverRule || 'none',
    carryOverCap: type.policy?.carryOverCap ?? '',
    allowsNegativeBalance: type.policy?.allowsNegativeBalance || false,
    requiresDocumentation: type.policy?.requiresDocumentation || false,
    documentationThresholdDays: type.policy?.documentationThresholdDays ?? '',
    // Rolling entitlement — flattened to match this form's shape; reassembled
    // into policy.entitlement on save.
    entitlementEnabled: !!type.policy?.entitlement,
    entitlementAmount: type.policy?.entitlement?.amount ?? 1,
    entitlementPeriod: type.policy?.entitlement?.period || 'week',
    entitlementMinLevel: type.policy?.entitlement?.minLevel ?? 0,
  });
  const [saving, setSaving] = useState(false);

  // No server draft for this modal form — the unsaved-work guard is the
  // only loss-prevention (e.g. a deploy reload while a long policy edit is
  // half-finished). href points back at the list page since the modal
  // itself isn't a distinct route.
  const initialFormRef = useRef(JSON.stringify(form));
  const isDirty = JSON.stringify(form) !== initialFormRef.current;
  useUnsavedGuard({
    id: `leave-type-${type.rowKey || 'new'}`,
    label: `Leave type: ${form.displayName || 'new'}`,
    href: '/admin/leave-types',
    dirty: isDirty,
  });

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    if (!form.typeId.trim()) {
      toast.error('Type ID is required');
      return;
    }
    if (!form.displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    setSaving(true);
    try {
      const policy = {
        accrualMethod: form.accrualMethod,
        requiresApproval: form.requiresApproval,
        approvalMode: form.requiresApproval ? form.approvalMode : undefined,
        countsAgainstBalance: form.countsAgainstBalance,
        maxConsecutiveDays: form.maxConsecutiveDays === '' ? null : Number(form.maxConsecutiveDays),
        minNoticeDays: Number(form.minNoticeDays) || 0,
        carryOverRule: form.carryOverRule,
        carryOverCap: form.carryOverRule === 'capped' ? Number(form.carryOverCap) || 0 : undefined,
        allowsNegativeBalance: form.allowsNegativeBalance,
        requiresDocumentation: form.requiresDocumentation,
        documentationThresholdDays: form.requiresDocumentation && form.documentationThresholdDays !== ''
          ? Number(form.documentationThresholdDays)
          : null,
        // null (not undefined) so clearing the tick actually removes an
        // existing rule rather than leaving the old one in place.
        entitlement: form.entitlementEnabled
          ? {
            amount: Number(form.entitlementAmount) || 1,
            period: form.entitlementPeriod,
            minLevel: Number(form.entitlementMinLevel) || 0,
          }
          : null,
      };

      await adminApi.upsertLeaveType(form.typeId.trim().toLowerCase(), {
        displayName: form.displayName.trim(),
        color: form.color,
        icon: form.icon,
        defaultAllowance: Number(form.defaultAllowance) || 0,
        isActive: form.isActive,
        policy,
      });
      toast.success(isNew ? `Created "${form.displayName}"` : `Updated "${form.displayName}"`);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate() {
    if (!window.confirm(`Deactivate "${type.displayName}"? It will no longer be available for new requests.`)) return;
    setSaving(true);
    try {
      await adminApi.deleteLeaveType(type.rowKey);
      toast.success(`Deactivated "${type.displayName}"`);
      onSaved();
    } catch (err) {
      toast.error(`Deactivate failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isNew ? 'Add leave type' : `Edit ${type.displayName}`}
      width="640px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {!isNew && type.isActive !== false && (
            <Button variant="danger" onClick={handleDeactivate} loading={saving}>
              Deactivate
            </Button>
          )}
          <Button onClick={handleSave} loading={saving}>
            {isNew ? 'Create type' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Type ID (slug)"
            value={form.typeId}
            onChange={(e) => set('typeId', e.target.value.replace(/[^a-z0-9-_]/gi, '').toLowerCase())}
            placeholder="e.g. vacation"
            disabled={!isNew}
          />
          <Input
            label="Display name"
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            placeholder="e.g. Vacation"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] font-medium mb-1.5 text-foreground">
              Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                style={{ width: 36, height: 36 }}
                className="border border-input rounded-sm bg-background cursor-pointer p-0.5"
              />
              <Input
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
          <Input
            label="Icon name"
            value={form.icon}
            onChange={(e) => set('icon', e.target.value)}
            placeholder="e.g. palm-tree"
          />
        </div>

        <Input
          label="Default annual allowance (days)"
          type="number"
          step="0.01"
          value={form.defaultAllowance}
          onChange={(e) => set('defaultAllowance', e.target.value)}
        />

        {/* Policy */}
        <div className="font-semibold text-sm mt-2 pb-1 border-b border-border">
          Policy
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Accrual method"
            value={form.accrualMethod}
            onChange={(e) => set('accrualMethod', e.target.value)}
            options={ACCRUAL_METHODS}
          />
          <Select
            label="Carry-over rule"
            value={form.carryOverRule}
            onChange={(e) => set('carryOverRule', e.target.value)}
            options={CARRY_OVER_RULES}
          />
        </div>

        {form.carryOverRule === 'capped' && (
          <Input
            label="Carry-over cap (days)"
            type="number"
            value={form.carryOverCap}
            onChange={(e) => set('carryOverCap', e.target.value)}
            placeholder="Max days to carry over"
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Min notice days"
            type="number"
            value={form.minNoticeDays}
            onChange={(e) => set('minNoticeDays', e.target.value)}
          />
          <Input
            label="Max consecutive days"
            type="number"
            value={form.maxConsecutiveDays}
            onChange={(e) => set('maxConsecutiveDays', e.target.value)}
            placeholder="No limit"
          />
        </div>

        {/* Boolean toggles */}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-2.5 cursor-pointer text-sm">
            <input type="checkbox" checked={form.requiresApproval} onChange={(e) => set('requiresApproval', e.target.checked)} />
            <span>Requires approval</span>
          </label>
          <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-2.5 cursor-pointer text-sm">
            <input type="checkbox" checked={form.countsAgainstBalance} onChange={(e) => set('countsAgainstBalance', e.target.checked)} />
            <span>Counts against balance</span>
          </label>
          <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-2.5 cursor-pointer text-sm">
            <input type="checkbox" checked={form.allowsNegativeBalance} onChange={(e) => set('allowsNegativeBalance', e.target.checked)} />
            <span>Allows negative balance</span>
          </label>
          <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-2.5 cursor-pointer text-sm">
            <input type="checkbox" checked={form.requiresDocumentation} onChange={(e) => set('requiresDocumentation', e.target.checked)} />
            <span>Requires documentation</span>
          </label>
        </div>

        {/* Rolling entitlement — a cap checked when the request is made.
            Nothing accrues, so unused days never carry forward. */}
        <div className="rounded-md border border-border bg-card p-3">
          <label className="flex items-center gap-2.5 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.entitlementEnabled}
              onChange={(e) => set('entitlementEnabled', e.target.checked)}
            />
            <span className="font-medium">Limit how often this can be taken</span>
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            Checked at request time. Nothing accrues and no balance is stored — unused
            days do not carry forward.
          </p>

          {form.entitlementEnabled && (
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium">Days</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={form.entitlementAmount}
                  onChange={(e) => set('entitlementAmount', e.target.value)}
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium">Per</label>
                <select
                  value={form.entitlementPeriod}
                  onChange={(e) => set('entitlementPeriod', e.target.value)}
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="week">calendar week</option>
                  <option value="month">calendar month</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium">Minimum level</label>
                <select
                  value={form.entitlementMinLevel}
                  onChange={(e) => set('entitlementMinLevel', e.target.value)}
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="0">Everyone</option>
                  <option value="20">Senior and above</option>
                  <option value="30">Manager and above</option>
                  <option value="40">Director and above</option>
                  <option value="50">Executive only</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {form.requiresApproval && (
          <Select
            label="Approval routing"
            value={form.approvalMode}
            onChange={(e) => set('approvalMode', e.target.value)}
            options={[
              { value: 'chain', label: 'Standard approval chain (assigned per person)' },
              { value: 'line_manager', label: 'Line manager only (single-step)' },
            ]}
          />
        )}

        {form.requiresDocumentation && (
          <Input
            label="Documentation required after (days)"
            type="number"
            value={form.documentationThresholdDays}
            onChange={(e) => set('documentationThresholdDays', e.target.value)}
            placeholder="e.g. 3"
          />
        )}

        {/* Active toggle */}
        <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-2.5 cursor-pointer text-sm mt-1">
          <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          <div>
            <div className="font-medium text-[13px]">Active</div>
            <div className="text-xs text-muted-foreground">
              Inactive types are hidden from employees but preserved for historical records.
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}
